// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, IntlContext, IntlShape } from "react-intl";

import { IAction } from "actions";
import { IState } from "states/states";
import { Site } from "states/sitestates";
import { isSet } from "shared/utils/utils";

interface ISiteWidgetProps {
  siteList: Site[] | undefined; // undefined if not loaded yet
  selectedSiteId: number | undefined;
  siteInfo: string | undefined;
}

interface ISiteWidgetDispatch {}

const mapStateToProps = (state: IState) => {
  const props: ISiteWidgetProps = {
    selectedSiteId: state.cached.selectedSite,
    siteList: state.site.siteList,
    siteInfo: state.para.parameter?.general?.site,
  };
  return props;
};

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISiteWidgetDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  noSiteInfo: {
    id: "No site information available!",
  },
  site: {
    id: "Current Site",
  },
});

class SiteWidgetComponent extends React.Component<ISiteWidgetProps> {
  static contextType = IntlContext;
  declare context: IntlShape;

  private urlify(text: string) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const detectedUrls: { url: string }[] = [];
    const textsToShow: string[] = [];
    text.replace(urlRegex, (url) => {
      detectedUrls.push({ url });
      return url;
    });

    detectedUrls.forEach((url) => {
      const textToSplit = textsToShow.pop() ?? text;
      textsToShow.push(...textToSplit.split(url.url));
    });

    // if no urls have been detected, add at least the initial text to our output list!
    textsToShow.length === 0 && textsToShow.push(text);

    return { detectedUrls, textsToShow };
  }

  public render() {
    const selectedSite = this.props.siteList?.find(
      (curSite) => curSite.id === this.props.selectedSiteId,
    );
    const { detectedUrls, textsToShow } = this.urlify(
      this.props.siteInfo ?? this.context.formatMessage(messages.noSiteInfo),
    );

    return (
      <>
        <div id="siteinfo">
          <div className="infobox">
            <span style={{ fontWeight: "bold" }}>{`${this.context.formatMessage(
              messages.site,
            )}: `}</span>
            <span>{selectedSite?.name}</span>
          </div>
          <div className="parameterSiteInfo">
            <div className="infobox">
              {textsToShow.map((text, curIdx) => (
                <div key={`siteinfo${curIdx}`}>
                  <div>
                    {curIdx > 0 && text.startsWith("\n\n") ? text.replace("\n\n", "\n") : text}
                  </div>
                  {isSet(detectedUrls.length > curIdx) && (
                    <a href={detectedUrls[curIdx]?.url ?? ""} target="_blank" rel="noreferrer">
                      {detectedUrls[curIdx]?.url}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SiteWidgetComponent);
